// Razorpay checkout loader (singleton)
let _loadingPromise = null;

export function loadRazorpay() {
  if (window.Razorpay) return Promise.resolve(true);
  if (_loadingPromise) return _loadingPromise;
  _loadingPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => { _loadingPromise = null; reject(new Error("Razorpay SDK failed to load")); };
    document.body.appendChild(s);
  });
  return _loadingPromise;
}

// ── In-app browser detection (kept for analytics/reference) ──────────────
const IN_APP_UA_RE = /(FBAN|FBAV|FB_IAB|FBIOS|Instagram|Line\/|Twitter|TwitterAndroid|Snapchat|Pinterest|MicroMessenger|GSA\/)/i;

export function isInAppBrowser() {
  try {
    const ua = navigator.userAgent || navigator.vendor || "";
    if (IN_APP_UA_RE.test(ua)) return true;
    // Generic Android WebView marker: "; wv)" in the UA string.
    if (/;\s*wv\)/.test(ua)) return true;
    return false;
  } catch {
    return false;
  }
}

export function isAndroid() {
  return /Android/i.test(navigator.userAgent || "");
}

// UTF-8 safe base64 (names may contain Devanagari etc.), URL-encoded for query use.
export function encodeCheckoutPayload(options) {
  const theme = options.theme || {};
  const safe = {
    key: options.key,
    order_id: options.order_id,
    amount: options.amount,
    currency: options.currency || "INR",
    name: options.name,
    description: options.description,
    prefill: options.prefill || {},
    notes: options.notes || {},
    theme_color: theme.color || "#A88838",
    callback_url: options.callback_url,
  };
  return encodeURIComponent(btoa(unescape(encodeURIComponent(JSON.stringify(safe)))));
}
export function decodeCheckoutPayload(d) {
  try {
    return JSON.parse(decodeURIComponent(escape(atob(decodeURIComponent(d)))));
  } catch {
    return null;
  }
}

// Open the Razorpay JS popup (inline overlay) immediately — the same instant
// experience as Swiggy/Zomato. redirect:true so a successful payment posts to
// callback_url and the backend fulfils the report. webview_intent:true is
// Razorpay's official flag to trigger native UPI apps (GPay/PhonePe/Paytm)
// from inside in-app browser WebViews (Instagram/Facebook).
export async function openRazorpayPopup(options) {
  await loadRazorpay();
  const rzp = new window.Razorpay({ redirect: true, webview_intent: true, ...options });
  rzp.open();
}

// Report checkout launcher.
//   • ANDROID + Instagram/Facebook in-app browser → ONE-TAP jump to Chrome
//     (the ONLY place UPI apps GPay/PhonePe/Paytm reliably open — Instagram's
//     Android WebView blocks the app-switch; confirmed by Razorpay/Google).
//     We force com.android.chrome (preinstalled on ~all Android phones) and
//     land on the tiny static /pay.html which AUTO-opens Razorpay — no extra
//     taps, no React reload. Buyer taps: Pay → OS "Continue" → pick GPay.
//   • iPhone in-app + ALL normal browsers → instant inline popup (UPI works
//     fine there), no browser hand-off.
export async function launchReportCheckout(options) {
  if (isInAppBrowser() && isAndroid()) {
    const payload = encodeCheckoutPayload(options);
    const host = window.location.host;
    const httpsUrl = `${window.location.protocol}//${host}/pay.html?d=${payload}`;
    const intentUrl =
      `intent://${host}/pay.html?d=${payload}#Intent;scheme=https;` +
      `package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(httpsUrl)};end`;
    window.location.href = intentUrl;
    // Safety net: if the intent didn't navigate away (rare WebViews that ignore
    // intent:// without firing the fallback), open the https pay page so the
    // buyer is never stuck (cards still work there).
    setTimeout(() => { try { window.location.href = httpsUrl; } catch (e) {} }, 1500);
    return { handoff: true };
  }
  await openRazorpayPopup(options);
  return { handoff: false };
}

export function formatINR(amount) {
  if (amount === 0 || amount === "0" || amount === 0.0) return "Free";
  const n = Number(amount || 0);
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);
}
